import { useEffect, useMemo, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { ref, getDownloadURL } from "firebase/storage";
import { onAuthStateChanged } from "firebase/auth";
import { auth, db, storage } from "../../Firebase";
import "./Public.css";
import Header from "../../components/Header";
import Footer from "../../components/Footer";

function shortId(id) {
  if (!id) return "";
  const s = String(id);
  return s.length <= 10 ? s : `${s.slice(0, 4)}…${s.slice(-4)}`;
}

function formatDate(v) {
  if (!v) return "";
  // supports "YYYY-MM-DD" or Firestore Timestamp
  if (typeof v === "object" && v?.toDate) {
    const d = v.toDate();
    return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  }
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

function normalizePriority(p) {
  const v = (p || "").toLowerCase();
  if (v.includes("immed")) return "Immediate";
  if (v.includes("near")) return "Near-term";
  return "Monitor";
}

function priorityClass(p) {
  const v = normalizePriority(p);
  if (v === "Immediate") return "prio prio-immediate";
  if (v === "Near-term") return "prio prio-nearterm";
  return "prio prio-monitor";
}

function deriveCounts(findings = []) {
  let immediate = 0, nearTerm = 0, monitor = 0;
  for (const f of findings) {
    const p = normalizePriority(f?.priority);
    if (p === "Immediate") immediate += 1;
    else if (p === "Near-term") nearTerm += 1;
    else monitor += 1;
  }
  return { immediate, nearTerm, monitor };
}

function deriveRecommendations(findings = []) {
  return findings
    .filter((f) => f?.recommendation)
    .map((f) => {
      const p = normalizePriority(f.priority);
      return {
        priority: p,
        recommendation: f.recommendation,
        location: f.location || "",
        timeline: p === "Immediate" ? "0–30 days" : p === "Near-term" ? "30–90 days" : "Monitor / next service cycle",
        notes: f.notes || ""
      };
    });
}

export default function ReportPublic() {
  const { blobId } = useParams();
  const [searchParams] = useSearchParams();
  const wantsPreview = searchParams.get("preview") === "1";

  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState(null);
  const [error, setError] = useState("");
  const [authReady, setAuthReady] = useState(false);
  const [adminUser, setAdminUser] = useState(null);

  // photo URL cache: { [storagePath]: downloadUrl }
  const [photoUrls, setPhotoUrls] = useState({});

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (user) => {
      setAdminUser(user || null);
      setAuthReady(true);
    });

    return () => unsub();
  }, []);

  useEffect(() => {
    document.title = "Inspection Report | Crowned Roofing";
  }, []);

  useEffect(() => {
    if (!authReady) return;

    let active = true;

    async function load() {
      setLoading(true);
      setError("");
      setReport(null);
      setPhotoUrls({});

      try {
        const snap = await getDoc(doc(db, "publicReports", blobId));
        if (!snap.exists()) {
          if (active) setError("Report not found. Please verify the link.");
          return;
        }

        const data = snap.data();

        const canPreviewDraft = wantsPreview && !!adminUser;

        if (data.status && data.status !== "published" && !canPreviewDraft) {
          if (active) setError("This report is not available.");
          return;
        }

        const rpt = { id: snap.id, ...data };

        if (!active) return;
        setReport(rpt);

        // Resolve all Storage paths to download URLs (once)
        const paths = [];
        for (const f of rpt.findings || []) {
          for (const p of f?.photos || []) {
            if (p?.path) paths.push(p.path);
          }
        }

        // Dedup
        const unique = Array.from(new Set(paths));
        if (!unique.length) return;

        const pairs = await Promise.all(
          unique.map(async (path) => {
            try {
              const url = await getDownloadURL(ref(storage, path));
              return [path, url];
            } catch {
              return [path, ""]; // handle missing/denied gracefully
            }
          })
        );

        if (!active) return;
        const next = {};
        for (const [path, url] of pairs) next[path] = url;
        setPhotoUrls(next);
      } catch (e) {
        if (active) setError(e?.message || "Failed to load report.");
      } finally {
        if (active) setLoading(false);
      }
    }

    load();
    return () => {
      active = false;
    };
  }, [authReady, adminUser, blobId, wantsPreview]);

  const findings = report?.findings || [];
  const counts = useMemo(() => {
    if (report?.counts) return report.counts;
    return deriveCounts(findings);
  }, [report, findings]);

  const recommendations = useMemo(() => {
    if (!report) return [];
    if (Array.isArray(report.recommendations) && report.recommendations.length) {
      return report.recommendations;
    }
    return deriveRecommendations(findings);
  }, [report, findings]);

  if (loading || !authReady) {
    return (
      <div className="reportPage">
        <Header/>
        <div className="reportTopBar noPrint">
          <div className="rtb-inner">
            <div className="rtb-left">
              <Link to="/" className="rtb-brand">Crowned Roofing</Link>
              <span className="rtb-sep" />
              <span className="rtb-title">Inspection Report</span>
            </div>
          </div>
        </div>

        <div className="reportSheet">
          <div className="reportState">
            <h1>Loading report…</h1>
            <p>Please wait.</p>
          </div>
        </div>
        <Footer/>
      </div>
    );
}

  if (error || !report) {
    return (
      <div className="reportPage">
        <Header />
        <div className="reportTopBar noPrint">
          <div className="rtb-inner">
            <div className="rtb-left">
              <Link to="/" className="rtb-brand">Crowned Roofing</Link>
              <span className="rtb-sep" />
              <span className="rtb-title">Inspection Report</span>
            </div>
          </div>
        </div>

        <div className="reportSheet">
          <div className="reportState">
            <h1>Unable to load report</h1>
            <p>{error || "Unknown error."}</p>
            <div className="noPrint" style={{ marginTop: 14 }}>
              <Link to="/" className="stateLink">Return to site</Link>
            </div>
          </div>
        </div>
        <Footer/>
      </div>
    );
  }

  const preparedBy = report.preparedBy || {};

  return (
    <div className="reportPage">
        <Header />
      {/* Screen-only top bar */}
    <div className="print-logo" style={{backgroundImage: `url(${process.env.PUBLIC_URL}/images/logo.png)`}} />
    <div className="print-watermark" style={{backgroundImage: `url(${process.env.PUBLIC_URL}/images/logo-black.png)`}} />
      <div className="reportTopBar noPrint">
        <div className="rtb-inner">
          <div className="rtb-left">
            <Link to="/" className="rtb-brand">Crowned Roofing</Link>
            <span className="rtb-sep" />
            <span className="rtb-title">Inspection Report</span>
          </div>

          <div className="rtb-right">
            <button className="rtb-btn" onClick={() => window.print()}>Print / Save PDF</button>
          </div>
        </div>
      </div>

      <div className="reportSheet">
        {wantsPreview && adminUser ? (
          <div className="arcNotice noPrint" style={{ marginBottom: 16 }}>
            Draft preview mode. This report is not public unless its status is set to published.
          </div>
        ) : null}
        {/* COVER */}
        <header className="reportCover keepTogether">
          <div className="coverTop">
            <div>
              <div className="docKicker">{report.title || "Commercial Roof Inspection Report"}</div>
              <h1 className="docTitle">{report.propertyName || "Inspection Report"}</h1>
              <div className="docSub">
                <div>{report.addressLine1 || ""}</div>
                <div>{report.cityStateZip || ""}</div>
              </div>
            </div>

            <div className="coverMetaBox">
              <div className="metaRow">
                <span>Inspected</span>
                <strong>{formatDate(report.inspectedOn)}</strong>
              </div>
              <div className="metaRow">
                <span>Report Date</span>
                <strong>{formatDate(report.reportDate)}</strong>
              </div>
              <div className="metaRow">
                <span>Report ID</span>
                <strong>{shortId(report.id)}</strong>
              </div>
            </div>
          </div>

          <div className="coverGrid">
            <div className="coverCard">
              <div className="label">Prepared for</div>
              <div className="value">{report.preparedFor || "Client"}</div>
            </div>

            <div className="coverCard">
              <div className="label">Prepared by</div>
              <div className="value">
                {preparedBy.company || "Crowned Roofing"}
                <div className="mutedSmall">{preparedBy.phone || ""}</div>
                <div className="mutedSmall">{preparedBy.email || ""}</div>
              </div>
            </div>

            <div className="coverCard">
              <div className="label">Roof system(s)</div>
              <div className="value">
                {(report.roofSystems || []).length ? (
                  <div className="chipRow">
                    {report.roofSystems.map((t, i) => (
                      <span className="chip" key={i}>{t}</span>
                    ))}
                  </div>
                ) : (
                  <span className="mutedSmall">Not specified</span>
                )}
              </div>
            </div>

            <div className="coverCard">
              <div className="label">Overview</div>
              <div className="value">
                <div className="kv"><span>Approx. area</span><strong>{report.approxArea || "—"}</strong></div>
                <div className="kv"><span>Access</span><strong>{report.access || "—"}</strong></div>
              </div>
            </div>
          </div>
        </header>

        {/* EXEC SUMMARY */}
        <section className="reportSection keepTogether">
          <h2 className="sectionTitle">Executive Summary</h2>

          <div className="summaryGrid">
            <div className="summaryCard">
              <div className="summaryBullets">
                {(report.summaryBullets || []).map((b, i) => (
                  <div className="bullet" key={i}>
                    <span className="dot" />
                    <span>{b}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="summaryCard">
              <div className="countBox">
                <div className="countRow">
                  <span className="countLabel">Immediate concerns</span>
                  <strong className="countNum">{counts.immediate ?? 0}</strong>
                </div>
                <div className="countRow">
                  <span className="countLabel">Near-term repairs</span>
                  <strong className="countNum">{counts.nearTerm ?? 0}</strong>
                </div>
                <div className="countRow">
                  <span className="countLabel">Monitor items</span>
                  <strong className="countNum">{counts.monitor ?? 0}</strong>
                </div>
              </div>

              {(report.weather || report.occupancySensitivity) && (
                <div className="miniMeta">
                  {report.weather ? <div><span>Weather</span> <strong>{report.weather}</strong></div> : null}
                  {report.occupancySensitivity ? (
                    <div><span>Occupancy</span> <strong>{report.occupancySensitivity}</strong></div>
                  ) : null}
                </div>
              )}
            </div>
          </div>
        </section>

        {/* FINDINGS */}
        <section className="reportSection">
          <h2 className="sectionTitle">Findings</h2>

          {findings.length === 0 ? (
            <p className="mutedSmall">No findings listed.</p>
          ) : (
            findings.map((f, idx) => {
              const photos = f?.photos || [];
              return (
                <article className="findingCard" key={f.id || idx}>
                  <div className="findingTop">
                    <div className={priorityClass(f.priority)}>{normalizePriority(f.priority)}</div>
                    <div className="findingId">{f.id || `F-${String(idx + 1).padStart(2, "0")}`}</div>
                  </div>

                  <h3 className="findingTitle">{f.title || "Finding"}</h3>

                  {f.location ? <div className="findingLoc"><span>Location:</span> {f.location}</div> : null}

                  <div className="findingBody">
                    {f.condition ? (
                      <div className="findingBlock">
                        <div className="fbLabel">Condition</div>
                        <div className="fbText">{f.condition}</div>
                      </div>
                    ) : null}

                    {f.impact ? (
                      <div className="findingBlock">
                        <div className="fbLabel">Impact</div>
                        <div className="fbText">{f.impact}</div>
                      </div>
                    ) : null}

                    {f.recommendation ? (
                      <div className="findingBlock">
                        <div className="fbLabel">Recommendation</div>
                        <div className="fbText">{f.recommendation}</div>
                      </div>
                    ) : null}
                  </div>

                  {photos.length > 0 && (
                    <div className="photoGrid">
                      {photos.slice(0, 4).map((p, i) => {
                        const url = p?.path ? photoUrls[p.path] : "";
                        return (
                          <figure className="photo" key={p.path || i}>
                            {url ? (
                              <img src={url} alt={p.caption || "Inspection photo"} loading="lazy" />
                            ) : (
                              <div className="photoMissing">Image unavailable</div>
                            )}
                            {p.caption ? <figcaption>{p.caption}</figcaption> : null}
                          </figure>
                        );
                      })}
                    </div>
                  )}
                </article>
              );
            })
          )}
        </section>

        {/* RECOMMENDATIONS */}
        <section className="reportSection pageBreakBefore">
          <h2 className="sectionTitle">Recommendations Summary</h2>

          {recommendations.length === 0 ? (
            <p className="mutedSmall">No recommendations listed.</p>
          ) : (
            <div className="tableWrap">
              <table className="recTable">
                <thead>
                  <tr>
                    <th>Priority</th>
                    <th>Recommendation</th>
                    <th>Location</th>
                    <th>Timeline</th>
                    <th>Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {recommendations.map((r, i) => (
                    <tr key={i}>
                      <td>
                        <span className={priorityClass(r.priority)}>{normalizePriority(r.priority)}</span>
                      </td>
                      <td>{r.recommendation || ""}</td>
                      <td>{r.location || ""}</td>
                      <td>{r.timeline || ""}</td>
                      <td>{r.notes || ""}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* LIMITATIONS */}
        <section className="reportSection keepTogether">
          <h2 className="sectionTitle">Limitations</h2>
          <div className="limits">
            {(report.limitations || []).length ? (
              (report.limitations || []).map((t, i) => (
                <div className="bullet" key={i}>
                  <span className="dot" />
                  <span>{t}</span>
                </div>
              ))
            ) : (
              <p className="mutedSmall">
                Visual inspection only. No destructive testing performed unless noted. Conditions may vary over time.
              </p>
            )}
          </div>

          <div className="reportFooter">
            <div>
              <strong>{preparedBy.company || "Crowned Roofing"}</strong>
              <div className="mutedSmall">{preparedBy.phone || ""}</div>
              <div className="mutedSmall">{preparedBy.email || ""}</div>
            </div>
            <div className="mutedSmall">Report ID: {shortId(report.id)}</div>
          </div>
        </section>

        <div className="noPrint mutedSmall" style={{ marginTop: 18 }}>
          Tip: use <strong>Print → Save as PDF</strong> to archive this report.
        </div>
      </div>
      <Footer/>
    </div>
  );
}
